import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Layers, Box, Cpu, FileText, ArrowRight, Clock, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

export function DesignerDashboard() {
  const navigate = useNavigate();

  // Proyectos de diseño activos
  const { data: designProjects, isLoading: projectsLoading } = useQuery({
    queryKey: ["design-projects-active"],
    queryFn: async () => {
      const { data, error } = await api
        .from("design_projects")
        .select("id, project_number, name, status, progress, customer:customers(company_name)")
        .in("status", ["draft", "in_design", "in_machining", "pending_approval"])
        .order("updated_at", { ascending: false })
        .limit(6);
      if (error) throw error;
      return data;
    },
  });

  // Part Numbers recientes
  const { data: partNumbers, isLoading: pnLoading } = useQuery({
    queryKey: ["recent-part-numbers"],
    queryFn: async () => {
      const { data, error } = await api
        .from("part_numbers")
        .select("id, code, description, status, design_project:design_projects(name)")
        .order("created_at", { ascending: false })
        .limit(5);
      if (error) throw error;
      return data;
    },
  });

  // Órdenes de mecanizado pendientes
  const { data: machiningOrders, isLoading: machiningLoading } = useQuery({
    queryKey: ["pending-machining"],
    queryFn: async () => {
      const { data, error } = await api
        .from("machining_orders")
        .select("id, order_number, description, status, machine, estimated_hours")
        .in("status", ["pending", "in_progress"])
        .order("created_at", { ascending: false })
        .limit(5);
      if (error) throw error;
      return data;
    },
  });

  // Moldes en desarrollo
  const { data: moldsInDev } = useQuery({
    queryKey: ["molds-in-dev"],
    queryFn: async () => {
      const { data, error } = await api
        .from("molds")
        .select("id, code, name, status")
        .in("status", ["in_design", "in_machining", "pending_validation"])
        .limit(5);
      if (error) throw error;
      return data;
    },
  });

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      draft: "bg-gray-500",
      in_design: "bg-blue-500",
      in_machining: "bg-purple-500",
      pending_approval: "bg-yellow-500",
      approved: "bg-green-500",
      pending: "bg-yellow-500",
      in_progress: "bg-blue-500",
      completed: "bg-green-500",
    };
    return colors[status] || "bg-gray-500";
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      draft: "Borrador",
      in_design: "En Diseño",
      in_machining: "Mecanizando",
      pending_approval: "Pend. Aprobación",
      approved: "Aprobado",
      pending: "Pendiente",
      in_progress: "En Progreso",
      completed: "Completado",
    };
    return labels[status] || status;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold">Panel de Diseño & CNC</h2>
        <p className="text-muted-foreground">Proyectos, Part Numbers y Mecanizado</p>
      </div>

      {/* Métricas */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Layers className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{designProjects?.length || 0}</p>
                <p className="text-xs text-muted-foreground">Proyectos Activos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <FileText className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{partNumbers?.length || 0}</p>
                <p className="text-xs text-muted-foreground">Part Numbers</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                <Cpu className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{machiningOrders?.length || 0}</p>
                <p className="text-xs text-muted-foreground">Órdenes CNC</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
                <Box className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{moldsInDev?.length || 0}</p>
                <p className="text-xs text-muted-foreground">Moldes en Dev</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Proyectos de diseño */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-lg">Proyectos de Diseño</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate("/design")}>
            Ver todos <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          {projectsLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
            </div>
          ) : designProjects?.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No hay proyectos activos
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {designProjects?.map(project => (
                <div
                  key={project.id}
                  className="p-4 rounded-lg border hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/design/${project.id}`)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-medium">DP-{String(project.project_number).padStart(4, "0")}</p>
                      <p className="text-sm text-muted-foreground truncate max-w-[180px]">
                        {project.name}
                      </p>
                    </div>
                    <Badge className={getStatusBadge(project.status)}>
                      {getStatusLabel(project.status)}
                    </Badge>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Progreso</span>
                      <span>{project.progress || 0}%</span>
                    </div>
                    <Progress value={project.progress || 0} className="h-2" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Órdenes de mecanizado */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg">Mecanizado CNC</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate("/machining")}>
              Ver todas
            </Button>
          </CardHeader>
          <CardContent>
            {machiningLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-14" />)}
              </div>
            ) : machiningOrders?.length === 0 ? (
              <p className="text-center text-muted-foreground py-6">
                No hay órdenes pendientes
              </p>
            ) : (
              <div className="space-y-3">
                {machiningOrders?.map(order => (
                  <div
                    key={order.id}
                    className="flex items-center justify-between p-3 rounded-lg border"
                  >
                    <div>
                      <p className="font-medium">{order.order_number}</p>
                      <p className="text-xs text-muted-foreground">
                        {order.machine} • {order.estimated_hours}h est.
                      </p>
                    </div>
                    <Badge className={getStatusBadge(order.status)}>
                      {getStatusLabel(order.status)}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Part Numbers recientes */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg">Part Numbers Recientes</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate("/part-numbers")}>
              Ver todos
            </Button>
          </CardHeader>
          <CardContent>
            {pnLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-14" />)}
              </div>
            ) : partNumbers?.length === 0 ? (
              <p className="text-center text-muted-foreground py-6">
                No hay Part Numbers
              </p>
            ) : (
              <div className="space-y-3">
                {partNumbers?.map(pn => (
                  <div
                    key={pn.id}
                    className="flex items-center justify-between p-3 rounded-lg border"
                  >
                    <div>
                      <p className="font-medium font-mono">{pn.code}</p>
                      <p className="text-xs text-muted-foreground truncate max-w-[180px]">
                        {pn.description}
                      </p>
                    </div>
                    <Badge className={getStatusBadge(pn.status)}>
                      {getStatusLabel(pn.status)}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
